import { clavePara, esInteligente, type Campo } from './api';

/**
 * reglas.ts — quién llena cada columna de la Base de Datos Empleados.
 *
 * Están aquí, separadas de la pantalla, porque **espejan al servidor**
 * (`campo_editable_por_colaborador` y `columnas_sin_decidir` de
 * `employee_db_service.py`). Si las dos capas dejan de decir lo mismo, el
 * formulario de datos iniciales le pide al colaborador algo que el servidor
 * después descarta, sin que nada avise.
 */

/**
 * Las once que NO son marcables. `apply_entry_to_user` las traduce a
 * `company_id` / `area_id` / `manager_id`, que son los tres datos que deciden
 * **quién aprueba las solicitudes** de esa persona: si el colaborador las
 * declarara, estaría eligiendo a su propio aprobador. Espeja
 * `CAMPOS_BLOQUEADOS`.
 */
export const BLOQUEADAS = [
  'cedula', 'full_name', 'email', 'empresa', 'sede', 'cargo', 'area',
  'jefe_inmediato', 'fecha_ingreso', 'tipo_contrato', 'centro_costos',
];

/** De las columnas base, las que el colaborador sí llena. */
export const SUYAS_POR_DEFECTO = [
  'fecha_nacimiento', 'genero', 'telefono', 'ciudad_residencia', 'ciudad_laboral',
];

/**
 * Las **veinte** columnas base del maestro: su respuesta no depende de nadie,
 * así que no se pregunta por ellas.
 *
 * El vanilla listaba diecinueve. `fecha_retiro` se añadió al servidor el
 * 2026-08-26 y esta copia se quedó sin ella: el constructor preguntaba «¿quién
 * la llena?» por una columna base —y bloqueaba el guardado hasta responder—
 * mientras el servidor no lo pedía. Lo vigila
 * `tests/test_recursos_pantalla.py`, comparando contra `FIELDS`.
 */
export const BASE = [
  'cedula', 'full_name', 'email', 'empresa', 'sede', 'cargo', 'area', 'genero',
  'fecha_nacimiento', 'fecha_ingreso', 'fecha_retiro', 'estado', 'jefe_inmediato',
  'centro_costos', 'tipo_contrato', 'telefono', 'ciudad_residencia', 'ciudad_laboral',
  'tiene_hijos', 'num_hijos',
];

export function claveDe(campo: Campo): string {
  return campo.key || clavePara(campo.label);
}

/** ¿Esta columna la llena el colaborador? */
export function laLlenaElColaborador(campo: Campo): boolean {
  const k = claveDe(campo);
  if (BLOQUEADAS.includes(k) || esInteligente(campo.type) || campo.readonly) return false;
  if ('autogestion' in campo && campo.autogestion != null) return !!campo.autogestion;
  // Sin marca, una columna base cae en su clasificación original; una añadida
  // se cierra por seguridad, igual que `_AUTOGESTION_NUEVAS = False`.
  return BASE.includes(k) ? SUYAS_POR_DEFECTO.includes(k) : false;
}

/**
 * ¿Es una columna a la que todavía no se le respondió quién la llena?
 *
 * Una casilla no podía expresar esto: «sin marcar» significaba a la vez «el
 * área dijo que no» y «el área todavía no lo ha dicho», y esos dos casos llevan
 * a sitios opuestos. Por eso el selector tiene tres estados y el guardado exige
 * el tercero.
 */
export function sinDecidir(campo: Campo): boolean {
  // Una fila recién agregada y todavía sin etiqueta no se guarda, así que
  // tampoco se le pregunta nada.
  if (!(campo.label || '').trim()) return false;
  const k = claveDe(campo);
  if (BLOQUEADAS.includes(k) || esInteligente(campo.type) || campo.readonly) return false;
  if (BASE.includes(k)) return false;
  return !('autogestion' in campo) || campo.autogestion == null;
}

/** Las que faltan por responder, solo en el maestro. */
export function columnasSinDecidir(campos: Campo[], esMaestro: boolean): Campo[] {
  return esMaestro ? campos.filter(sinDecidir) : [];
}
